import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleAlert, Clock, Eye, EyeOff, Loader2, Moon, Sun } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LoginError } from '../contexts/loginError';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BrandLockup } from '../components/Layout/Sidebar';
import { BRAND } from '../lib/brand';

const NOTES = [
  'Вход открыт только аккаунтам с ролью «Админ».',
  'Действия с пользователями и деньгами пишутся в аудит-лог.',
  'Лимиты, тарифы и баннеры меняются без релиза приложения.',
];

interface LoginProps {
  isDark: boolean;
  setIsDark: (value: boolean) => void;
}

const Login: React.FC<LoginProps> = ({ isDark, setIsDark }) => {
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await login(loginIdentifier, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof LoginError ? err.message : 'Не удалось войти');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-wrap items-stretch bg-bg text-text">
      <main className="flex min-w-0 flex-[1_1_560px] flex-col bg-surface px-6 py-8 sm:px-10">
        <BrandLockup className="self-start" />

        <div className="flex flex-1 items-center justify-center py-10">
          <form
            onSubmit={handleSubmit}
            aria-labelledby="login-title"
            className="flex w-full max-w-[400px] flex-col gap-[18px]"
          >
            <div className="flex flex-col gap-1.5">
              <h1 id="login-title" className="m-0 font-display text-[28px] font-extrabold leading-tight tracking-[-0.01em]">
                {BRAND.name} · Админ-панель
              </h1>
              <span className="text-text-2">Войдите учётной записью с ролью «Админ».</span>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="login-id">Email или логин</Label>
              <Input
                id="login-id"
                type="text"
                value={loginIdentifier}
                onChange={(e) => setLoginIdentifier(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="username"
                className="h-11 px-3.5"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="login-pass">Пароль</Label>
              <span className="relative block">
                <Input
                  id="login-pass"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'login-err' : undefined}
                  className="h-11 pl-3.5 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  aria-pressed={showPassword}
                  aria-controls="login-pass"
                  className="absolute right-1 top-1 grid size-9 place-items-center rounded-[10px] text-text-2 outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {showPassword ? <EyeOff className="size-[18px]" strokeWidth={1.8} /> : <Eye className="size-[18px]" strokeWidth={1.8} />}
                </button>
              </span>
              {error && (
                <span id="login-err" role="alert" className="flex items-center gap-1.5 text-[13px] font-semibold text-neg">
                  <CircleAlert className="size-[15px] shrink-0" aria-hidden />
                  {error}
                </span>
              )}
            </div>

            <Button type="submit" size="lg" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" />
                  Вход…
                </>
              ) : (
                'Войти'
              )}
            </Button>

            <span className="flex items-center gap-2 text-xs text-text-2">
              <Clock className="size-3.5 shrink-0" strokeWidth={1.8} aria-hidden />
              <span>
                Попыток: <span className="tabular-nums">8 за 15 минут</span>, затем вход временно закрывается.
              </span>
            </span>
          </form>
        </div>

        <span className="text-xs text-text-3">Забыли пароль? Обратитесь к владельцу панели.</span>
      </main>

      <aside aria-label="О панели" className="flex min-w-0 flex-[1_1_480px] flex-col bg-hero px-6 py-8 text-on-hero sm:px-10">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setIsDark(!isDark)}
            aria-label={isDark ? 'Включить светлую тему' : 'Включить тёмную тему'}
            className="grid size-10 place-items-center rounded-xl border border-hero-track text-on-hero outline-none hover:bg-hero-track focus-visible:ring-2 focus-visible:ring-accent"
          >
            {isDark ? <Sun className="size-[18px]" strokeWidth={1.8} /> : <Moon className="size-[18px]" strokeWidth={1.8} />}
          </button>
        </div>
        <div className="flex max-w-[440px] flex-1 flex-col justify-center gap-[22px] py-10">
          <b className="font-display text-xl">Что важно знать</b>
          <ul className="m-0 flex list-none flex-col p-0">
            {NOTES.map((note, i) => (
              <li
                key={note}
                className="flex items-start gap-3.5 border-t border-hero-track py-3.5 last:border-b"
              >
                <span className="font-display font-bold text-accent tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-on-hero-2">{note}</span>
              </li>
            ))}
          </ul>
        </div>
        <span className="text-xs text-on-hero-2">Время сервера: Ташкент, UTC+5</span>
      </aside>
    </div>
  );
};

export default Login;
